/**
 * InvitationsPanel — tenant detail panel's Invitations section (TMMPDM-0004,
 * contract §2–4). Defaults to `status=PENDING`, with an "all" toggle. Per
 * PENDING row: Resend (rotates + re-sends, refreshes `expiresAt`) and Revoke
 * (confirm → row becomes `REVOKED`). Non-pending rows are read-only.
 *
 * `status` is rendered exactly as returned — it's server-COMPUTED, never
 * recomputed from timestamps here. A 409 `INVITATION_ALREADY_ACCEPTED`
 * (accepted between list-load and action) and a 404 (not found — treated
 * generically, never leaking detail) both just refetch the list.
 *
 * `refreshKey` lets the sibling MembersPanel trigger a refetch here after
 * `addMember` returns an invitation (202) — wired by `TenantDetailPage.tsx`.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState, ErrorState } from '@/components/page';
import { listInvitations, resendInvitation, revokeInvitation } from '@/api/saas/tenants';
import { PeopleInvitationsBlock } from '../partners/PeopleInvitationsBlock';
import { PeopleTableContainer } from '../partners/peopleLeafLayout';
import { ApiError, errorCopy } from '@/api/errors';
import { isStaffRole, type TenantInvitationDto } from '@/api/saas/types';
import { formatInstant } from '@/format';
import { StatusChip } from '@/status';

interface InvitationsPanelProps {
  tenantId: number;
  /** Bump this from the parent to force a refetch (e.g. after MembersPanel invites someone). */
  refreshKey?: number;
  /** Staff (tenant administrators) or everyone who is not staff (customers). */
  audience?: 'staff' | 'customer';
}

function matchesAudience(role: string, audience: 'staff' | 'customer'): boolean {
  return audience === 'staff' ? isStaffRole(role) : !isStaffRole(role);
}

export function InvitationsPanel({
  tenantId,
  refreshKey = 0,
  audience = 'staff',
}: InvitationsPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [invitations, setInvitations] = useState<TenantInvitationDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadCause, setLoadCause] = useState<unknown>(null);
  const [showAll, setShowAll] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<TenantInvitationDto | null>(null);
  // A refused revoke is said inside its confirm, which stays open (TMM-B4).
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const changeShowAll = (value: boolean) => {
    setLoading(true);
    setShowAll(value);
  };

  useEffect(() => {
    let cancelled = false;
    listInvitations(tenantId, showAll ? undefined : 'PENDING')
      .then((res) => {
        if (cancelled) return;
        setInvitations(res.filter((inv) => matchesAudience(inv.role, audience)));
        setLoadFailed(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadCause(err);
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, showAll, reloadToken, refreshKey, audience]);

  const refetch = () => {
    setLoading(true);
    setReloadToken((n) => n + 1);
  };

  /**
   * Shared mapping for resend/revoke failures: a 409 `INVITATION_ALREADY_ACCEPTED`
   * (accepted between list-load and action) or a 404 (not found — treated
   * generically, never leaking detail) both refetch the list so the row
   * reflects reality; any other failure just shows the generic message.
   */
  const describeActionError = (
    err: unknown,
    genericFallback: string,
  ): { message: string; refetchAfter: boolean } => {
    const apiErr = err instanceof ApiError ? err : null;
    if (apiErr?.code === 'INVITATION_ALREADY_ACCEPTED') {
      return {
        message: t(
          'tenantDetail.invitations.alreadyAccepted',
          'This invitation was already accepted. The list has been refreshed.',
        ),
        refetchAfter: true,
      };
    }
    if (apiErr?.status === 404) {
      return {
        message: t(
          'tenantDetail.invitations.notFound',
          'This invitation could not be found. The list has been refreshed.',
        ),
        refetchAfter: true,
      };
    }
    return { message: errorCopy(err, t, genericFallback), refetchAfter: false };
  };

  const handleResend = async (invitation: TenantInvitationDto) => {
    setActionError(null);
    setBusyId(invitation.id);
    try {
      await resendInvitation(tenantId, invitation.id);
      refetch();
    } catch (err) {
      const { message, refetchAfter } = describeActionError(
        err,
        t('tenantDetail.invitations.resendError', 'Could not resend invitation. Please try again.'),
      );
      setActionError(message);
      if (refetchAfter) refetch();
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmRevoke = async () => {
    const invitation = revokeTarget;
    if (!invitation) return;
    setActionError(null);
    setRevokeError(null);
    setBusyId(invitation.id);
    try {
      await revokeInvitation(tenantId, invitation.id);
      setRevokeTarget(null);
      refetch();
    } catch (err) {
      const { message, refetchAfter } = describeActionError(
        err,
        t('tenantDetail.invitations.revokeError', 'Could not revoke invitation. Please try again.'),
      );
      setRevokeError(message);
      if (refetchAfter) refetch();
    } finally {
      setBusyId(null);
    }
  };

  const ready = !loading && !loadFailed && invitations !== null;

  return (
    <Box sx={{ mt: 3 }}>
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {loadFailed && !loading ? (
        <ErrorState
          error={loadCause}
          message={t('tenantDetail.invitations.loadError', 'Could not load invitations.')}
          onRetry={refetch}
        />
      ) : (
        <PeopleInvitationsBlock
          ready={ready}
          count={invitations?.length ?? 0}
          showAll={showAll}
          onShowAll={changeShowAll}
        >
          {invitations ? (
            <PeopleTableContainer>
              <Table
                aria-label={t('tenantDetail.invitations.tableAriaLabel', 'Tenant invitations')}
                size="small"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('tenantDetail.invitations.col.email', 'Email')}</TableCell>
                    <TableCell>{t('tenantDetail.invitations.col.role', 'Role')}</TableCell>
                    <TableCell>{t('tenantDetail.invitations.col.status', 'Status')}</TableCell>
                    <TableCell>{t('tenantDetail.invitations.col.created', 'Created')}</TableCell>
                    <TableCell>{t('tenantDetail.invitations.col.expires', 'Expires')}</TableCell>
                    <TableCell align="right">
                      {t('tenantDetail.invitations.col.actions', 'Actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <EmptyState
                          title={
                            showAll
                              ? t('tenantDetail.invitations.emptyAll', 'No invitations.')
                              : t('tenantDetail.invitations.empty', 'No invitations pending.')
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    invitations.map((invitation) => {
                      const busy = busyId === invitation.id;
                      const pending = invitation.status === 'PENDING';
                      return (
                        <TableRow key={invitation.id}>
                          <TableCell>{invitation.email}</TableCell>
                          <TableCell>
                            {t(`tenantDetail.members.role.${invitation.role}`, invitation.role)}
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              status={invitation.status}
                              domain="invitation"
                              label={t(
                                `tenantDetail.invitations.status.${invitation.status}`,
                                invitation.status,
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            {formatInstant(invitation.createdAt, i18n.language)}
                          </TableCell>
                          <TableCell>
                            {formatInstant(invitation.expiresAt, i18n.language)}
                          </TableCell>
                          <TableCell align="right">
                            {pending ? (
                              <>
                                <Button
                                  size="small"
                                  disabled={busy}
                                  onClick={() => void handleResend(invitation)}
                                >
                                  {t('tenantDetail.invitations.resendButton', 'Resend')}
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  disabled={busy}
                                  onClick={() => setRevokeTarget(invitation)}
                                >
                                  {t('tenantDetail.invitations.revokeButton', 'Revoke')}
                                </Button>
                              </>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </PeopleTableContainer>
          ) : null}
        </PeopleInvitationsBlock>
      )}

      <ConfirmDialog
        open={revokeTarget !== null}
        title={t('tenantDetail.invitations.revokeConfirmTitle', 'Revoke invitation?')}
        consequence={t(
          'tenantDetail.invitations.revokeConfirmBody',
          'This revokes the pending invitation to {{email}}. They will no longer be able to accept it.',
          { email: revokeTarget?.email ?? '' },
        )}
        confirmLabel={t('tenantDetail.invitations.revokeButton', 'Revoke')}
        destructive
        busy={busyId !== null}
        error={revokeError}
        onConfirm={() => void handleConfirmRevoke()}
        onCancel={() => {
          setRevokeTarget(null);
          setRevokeError(null);
        }}
      />
    </Box>
  );
}
